import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { API_GATEWAY_URL, AUTH_TOKEN_KEY, NOTIFICATIONS_WIDGET_URL, AUTH_WS_URL } from '../api/config';
import { refresh } from '../api/refresh';

// The notifications bell and page live in auth-user-services (notifications-widget/) and are loaded
// at runtime as custom elements, so fixes deployed there show up here without a rebuild.

function useWidgetScript() {
  useEffect(() => {
    if (!NOTIFICATIONS_WIDGET_URL || document.querySelector('script[data-notifications-widget]')) return;
    const script = document.createElement('script');
    script.src = NOTIFICATIONS_WIDGET_URL;
    script.async = true;
    script.dataset.notificationsWidget = '';
    document.head.appendChild(script);
  }, []);
}

// The app's single-flight refresh (the same one the axios interceptor and the clock widget use)
const refreshToken = () => refresh();

// No ui-kit palette in the admin: the widget gets the MUI theme's primary colour explicitly
function useWidgetElement() {
  const ref = useRef(null);
  useWidgetScript();
  useEffect(() => {
    if (ref.current) ref.current.refreshToken = refreshToken;
  }, []);
  return ref;
}

/** Sidebar footer row with the unread badge and the dropdown. */
export function NotificationsBellWidget({ collapsed = false }) {
  const ref = useWidgetElement();
  const primary = useTheme().palette.primary.main;
  if (!NOTIFICATIONS_WIDGET_URL) return null;
  return (
    <anyemp-notifications-bell
      ref={ref}
      gateway-url={API_GATEWAY_URL}
      ws-url={AUTH_WS_URL}
      token-key={AUTH_TOKEN_KEY}
      primary-color={primary}
      collapsed={collapsed ? '' : undefined}
    />
  );
}

/** The /notifications page. */
export function NotificationsPageWidget() {
  const ref = useWidgetElement();
  const primary = useTheme().palette.primary.main;
  if (!NOTIFICATIONS_WIDGET_URL) return null;
  return <anyemp-notifications-page ref={ref} gateway-url={API_GATEWAY_URL} ws-url={AUTH_WS_URL} token-key={AUTH_TOKEN_KEY} primary-color={primary} />;
}

/** Links from the widget to this app's pages go through the SPA router (no reload). */
export function useNotificationsNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    const onNavigate = (e) => {
      if (!e.detail?.path) return;
      e.preventDefault();
      navigate(e.detail.path);
    };
    window.addEventListener('anyemp:navigate', onNavigate);
    return () => window.removeEventListener('anyemp:navigate', onNavigate);
  }, [navigate]);
}

/** Unread total published by the widget — the badge on the Notifications menu item. */
export function useNotificationsUnreadCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const onCounts = (e) => setCount(e.detail?.total ?? 0);
    window.addEventListener('anyemp:notifications-counts', onCounts);
    return () => window.removeEventListener('anyemp:notifications-counts', onCounts);
  }, []);
  return count;
}
